
import React from 'react';
import { Property, AgentProfile } from '../types';
import { MapPin, Bed, Bath, Share2, MessageCircle, MoreHorizontal, Sofa, ShieldCheck, ChevronRight, EyeOff } from 'lucide-react';

interface PropertyPostProps {
  property: Property;
  agent: AgentProfile;
  onDetail: (p: Property) => void;
  onAgentClick?: (agentId: string) => void;
  isOffline?: boolean;
}

export const PropertyPost: React.FC<PropertyPostProps> = ({ property, agent, onDetail, onAgentClick, isOffline }) => {
  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.currency || 'USD',
    maximumFractionDigits: 0,
  });

  const handleAgentClick = () => {
    if (onAgentClick) onAgentClick(agent.id);
  };

  const getPriceColorClass = (category: string) => {
    switch (category) {
      case 'Sale': return 'text-blue-600 dark:text-blue-400';
      case 'Rent': return 'text-green-600 dark:text-green-400';
      case 'Short Let': return 'text-orange-600 dark:text-orange-400';
      case 'JV': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-gray-900 dark:text-white';
    }
  };

  return (
    <div className="bg-white dark:bg-black border-y md:border border-gray-100 dark:border-zinc-800 md:rounded-lg overflow-hidden mb-8 shadow-sm">
      {/* Post Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={handleAgentClick}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 p-[1.5px]">
            <div className="w-full h-full rounded-full bg-white dark:bg-black p-[1px]">
               <img src={agent.avatar} alt={agent.name} className="w-full h-full rounded-full object-cover" loading="lazy" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <p className="text-xs font-black dark:text-white leading-none group-hover:underline">{agent.business_name}</p>
              {agent.verification_status === 'verified' && <CheckCircle2 className="w-3 h-3 text-blue-500 fill-blue-500 text-white" />}
            </div>
            <p className="text-[10px] text-gray-500 dark:text-zinc-500 font-medium leading-tight">{property.location}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           {isOffline && (
             <span className="px-2 py-1 bg-red-600 text-white text-[8px] font-black uppercase tracking-widest rounded-md flex items-center gap-1">
               <EyeOff className="w-2.5 h-2.5" /> Offline
             </span>
           )}
           <button className="p-1 active:scale-90 transition-transform"><MoreHorizontal className="w-5 h-5 text-gray-400 dark:text-zinc-600" /></button>
        </div>
      </div>

      {/* Post Image */}
      <div 
        className="aspect-square bg-gray-100 dark:bg-zinc-900 cursor-pointer overflow-hidden relative"
        onClick={() => onDetail(property)}
      >
        <img 
          src={property.images[0]} 
          alt={property.title} 
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
          loading="lazy"
        />
        
        {/* Category Overlay Tag */}
        <div className="absolute top-4 left-4">
           <span className="px-2.5 py-1 bg-black/50 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-widest rounded-lg border border-white/20">
             FOR {property.category || 'SALE'}
           </span>
        </div>

        {/* Status Mini Icons */}
        <div className="absolute bottom-4 right-4 flex gap-1.5">
           {property.is_furnished && <div className="bg-white/90 p-1.5 rounded-lg shadow-lg"><Sofa className="w-3.5 h-3.5 text-gray-900" /></div>}
           {property.is_serviced && <div className="bg-white/90 p-1.5 rounded-lg shadow-lg"><ShieldCheck className="w-3.5 h-3.5 text-gray-900" /></div>}
        </div>
      </div>

      {/* Simplified Post Information */}
      <div className="p-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex flex-col">
              <p className={`text-lg font-black tracking-tighter ${getPriceColorClass(property.category)}`}>
                {priceFormatter.format(property.price)}
              </p>
              {property.is_negotiable && <span className="text-[8px] font-black uppercase text-amber-500 tracking-widest">Negotiable</span>}
            </div>
            <button 
              onClick={() => onDetail(property)}
              className="px-4 py-2 bg-gray-50 dark:bg-zinc-900 rounded-xl flex items-center gap-2 group active:scale-95 transition-all"
            >
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-400">View Detail</span>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
          
          <div className="text-sm dark:text-gray-200">
            <span className="font-black mr-2 text-gray-900 dark:text-white cursor-pointer hover:underline" onClick={handleAgentClick}>{agent.business_name.toLowerCase().replace(/\s+/g, '_')}</span>
            <span className="font-medium text-gray-800 dark:text-zinc-300">{property.title}</span>
          </div>
          
          <div className="flex items-center gap-4 mt-3 py-3 border-y border-gray-50 dark:border-zinc-900/50">
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-zinc-500">
              <Bed className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-widest">{property.bedrooms || 0} Beds</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-zinc-500">
              <Bath className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-widest">{property.bathrooms || 0} Baths</span>
            </div>
            {property.area && (
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-zinc-500">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-widest">{property.area} sqm</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckCircle2 = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
